import { Autocomplete, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getPaymentMethodsInUse } from 'src/shared/services/memberService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PaymentMethodInUse } from '../models/paymentMethodInUse';

type Option = { value: string; label: string };

// Periodicitet → i18n-nyckel-suffix, matchar detaljvyns display-funktioner exakt
// (autogiro/stripe/swish: 6 = year).
const autogiroSuffix: Record<number, string> = { 1: 'month', 2: 'twoMonths', 3: 'threeMonths', 4: 'fourMonths', 5: 'sixMonths', 6: 'year' };
const stripeSuffix: Record<number, string> = { 1: 'month', 2: 'twoMonths', 3: 'threeMonths', 4: 'fourMonths', 5: 'sixMonths', 6: 'year' };

// Filtret bygger listan dynamiskt från de betalmetoder+periodiciteter som faktiskt används (inga tomma val).
export const MemberAdvancedSearchPaymentMethods = () => {
  const { t } = useTranslation(['member', 'common']);
  const { setValue, watch } = useFormContext();
  const isMounted = useIsMounted();
  const [options, setOptions] = useState<Option[]>([]);

  const selected: string[] = watch('paymentMethods') || [];

  const buildLabel = (c: PaymentMethodInUse): string => {
    if (c.method === 'autogiro') return t(`member:autogiro.${autogiroSuffix[c.periodicity] ?? 'month'}`);
    if (c.method === 'stripe') return t(`member:stripe.${stripeSuffix[c.periodicity] ?? 'month'}`);
    if (c.method === 'swish') return `${t('member:swishRecurring')} (${t(`member:stripePeriod.${stripeSuffix[c.periodicity] ?? 'month'}`)})`;
    return `${c.method}:${c.periodicity}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getPaymentMethodsInUse();
        if (isMounted()) {
          setOptions((res.data || []).map((c) => ({ value: `${c.method}:${c.periodicity}`, label: buildLabel(c) })));
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  return (
    <Autocomplete
      multiple
      size='small'
      options={options}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={options.filter((o) => selected.includes(o.value))}
      onChange={(_, newValue) =>
        setValue(
          'paymentMethods',
          newValue.map((o) => o.value),
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('member:memberAdvancedSearch.paymentMethods')}
          placeholder={selected.length > 0 ? undefined : t('common:form.all')}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
};
